import { getValuesForSelectedOptions } from 'commons/productUtils';
import { find } from 'lodash';
import avo from 'analytics/avo';
import { getPalomaPixelData } from 'commons/localStorage';
import * as Sentry from '@sentry/react';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePdpSelectedOptionsVariantSku from 'components/consumer/PDP/state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';
import useUserData from 'data-hooks/useUserData';
import removeNull from '../../commons/removeNull';
import { palomaPixelAddtoCart } from '../../commons/analytics';
export const getHeapAddToCartData = (product, eventData) => {
  eventData.exposed_wood = product.exp_wood_product;
  eventData.product_source = product.efg_flag === 0 ? 'internal' : 'external';
  eventData.quantity = 1;
  return removeNull(eventData);
};
export const getSegmentCartItemData = (eventData, cartItem = null) => {
  let variant = eventData.fabric_name;
  if (cartItem) {
    eventData.full_price = cartItem?.price_pre_discount || eventData.full_price;
    eventData.price = cartItem?.price_post_discount || eventData.price;
    variant = cartItem?.options.map(option => option.name).join(', ');
  }
  const segmentRes = {
    name: eventData.product_name,
    product_id: eventData.product_id,
    quantity: eventData.quantity || 1,
    cart_id: parseInt(eventData?.cart_id, 10),
    category: eventData.product_type,
    fabric_id: eventData?.fabric_id,
    fabric_name: eventData?.fabric_name,
    full_price: eventData.full_price,
    price: eventData.price,
    product_family: eventData.product_family,
    quickship: eventData.quickship,
    sku: eventData.sku,
    variant
  };
  return removeNull(segmentRes);
};
export const getGTMAddToCartData = ({
  isClearance = false,
  isDecideLater = false,
  isFavorited = false,
  isQuickShip = false,
  optionPrice,
  options,
  price,
  product,
  selectedOptions,
  sku
}) => {
  const {
    fabric,
    wood
  } = getValuesForSelectedOptions(options, selectedOptions);
  return {
    fabric_id: fabric?.id || 0,
    fabric_name: isDecideLater ? 'Decide Later' : fabric?.value || '',
    fabric_color: isDecideLater ? 'Decide Later' : fabric?.color || '',
    favorited: isFavorited,
    product_id: product?.id || 0,
    product_name: product?.name || '',
    price: (price?.final_price ?? 0) + optionPrice,
    full_price: (price?.original_price ?? 0) + optionPrice,
    product_family: product?.family?.name || '',
    product_type: product?.type.name || '',
    quickship: isQuickShip,
    sku: product ? isDecideLater ? `${product.sku}-DL${sku}` : product.sku + sku : '',
    wood_stain: wood?.value || '',
    wood_stain_id: wood?.id || 0,
    is_clearance: isClearance,
    rating: product?.meta.reviews?.average_score?.toFixed(2) || 0,
    reviews: product?.meta.reviews?.count || 0
  };
};
const useUserCartEvent = (basicDetails, optionData, priceData, isClearance, isFavorited) => {
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();
  const {
    email: userEmail
  } = useUserData();
  const removeFromCartEvent = (cartItem, cartToken = '') => {
    if (cartItem && cartItem.id) {
      const quantity = 0;

      // TODO: Once we have found the root cause of the error that is
      // occasionally being thrown by removeFromCartEvent (CON-3259), we should
      // remove this try/catch block and revert to standard error handling.
      try {
        let eventData = getGTMAddToCartData({
          isClearance,
          isDecideLater: pdpSelectedCustomizerTab === 'decide-later',
          isFavorited,
          isQuickShip: optionData?.stock?.isSelectedOptionsQuickship,
          optionPrice: priceData?.optionPrice,
          options: optionData.options,
          price: priceData?.price,
          product: basicDetails,
          selectedOptions: optionData.selectedOptions,
          sku: selectedOptionsVariantSku
        });
        eventData = {
          ...eventData,
          cart_id: cartItem.id,
          quantity
        };
        avo.productRemoved({
          category: eventData.product_type,
          name: eventData.product_name,
          price: parseFloat(eventData.price),
          productId: eventData.product_id,
          quantity: eventData.quantity || 1,
          sku: eventData.sku,
          variant: cartItem?.options.map(option => option.name).join(', '),
          productFamily: cartItem?.product?.family?.name || '',
          cartId: cartToken,
          fabricName: eventData?.fabric_name,
          fabricColor: eventData?.fabric_color,
          currency: 'USD'
        });
      } catch (err) {
        Sentry.withScope(scope => {
          scope.setExtra('getGTMAddToCartDataArguments', {
            isClearance,
            isDecideLater: pdpSelectedCustomizerTab === 'decide-later',
            isFavorited,
            isQuickShip: optionData?.stock?.isSelectedOptionsQuickship,
            optionPrice: priceData?.optionPrice,
            options: optionData?.options,
            price: priceData?.price,
            product: basicDetails,
            selectedOptions: optionData?.selectedOptions,
            sku: selectedOptionsVariantSku
          });
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      }
    }
  };
  const addToCartEvent = (cart = null, cartToken_0 = '', mode = 'pdp') => {
    let cartItem_0 = null;
    let quantity_0 = 1;
    let eventData_0 = getGTMAddToCartData({
      isClearance,
      isDecideLater: pdpSelectedCustomizerTab === 'decide-later',
      isFavorited,
      isQuickShip: optionData?.stock?.isSelectedOptionsQuickship,
      optionPrice: priceData?.optionPrice,
      options: optionData.options,
      price: priceData?.price,
      product: basicDetails,
      selectedOptions: optionData.selectedOptions,
      sku: selectedOptionsVariantSku
    });
    if (Array.isArray(cart) && cart.length > 0) {
      cartItem_0 = find(cart, {
        product_id: basicDetails.id
      });
      quantity_0 = cartItem_0 && cartItem_0?.quantity ? cartItem_0.quantity : 1;
      eventData_0 = {
        ...eventData_0,
        quantity: quantity_0
      };
    }
    const variant = cartItem_0?.options.map(option_0 => option_0.name).join(', ') || '';
    avo.productAdded({
      productId: +eventData_0.product_id,
      nonInteraction: 1,
      price: parseFloat(eventData_0?.price),
      fullPrice: parseFloat(eventData_0?.full_price),
      quantity: +quantity_0,
      sku: eventData_0?.sku,
      cartId: cartToken_0,
      name: eventData_0?.product_name,
      variant,
      quickship: eventData_0.quickship,
      fabricName: eventData_0?.fabric_name,
      fabricColor: eventData_0?.fabric_color,
      location: mode === 'quickview' ? avo.Location.LOOKBOOKS : avo.Location.PDP,
      component: mode === 'quickview' ? avo.Component.QUICKVIEW_MODAL : undefined,
      currency: 'USD'
    });
    const paloma = getPalomaPixelData();
    if (paloma?.paloma_subscriber_id && paloma?.paloma_conversation_id) {
      palomaPixelAddtoCart({
        subscriber_id: paloma?.paloma_subscriber_id,
        conversation_id: paloma?.paloma_conversation_id,
        email: userEmail || null,
        products: [{
          product_id: +eventData_0.product_id,
          category: eventData_0.productType,
          name: eventData_0?.product_name,
          price: parseFloat(eventData_0?.price),
          quantity: quantity_0,
          sku: eventData_0?.sku,
          variant,
          is_clearance: isClearance
        }]
      });
    }
  };
  return {
    addToCartEvent,
    removeFromCartEvent
  };
};
export default useUserCartEvent;